import { useState, useCallback, useRef } from 'react';
import type { ArticleSearchResult } from '../services/articleService';
import { articleService } from '../services/articleService';

interface UseArticleSearchOptions {
  limit?: number;
  debounceMs?: number;
}

interface UseArticleSearchReturn {
  suggestions: ArticleSearchResult[];
  isLoading: boolean;
  error: string | null;
  searchArticles: (query: string) => Promise<void>;
  clearSuggestions: () => void;
}

export const useArticleSearch = (options: UseArticleSearchOptions = {}): UseArticleSearchReturn => {
  const { limit = 3, debounceMs = 300 } = options;

  const [suggestions, setSuggestions] = useState<ArticleSearchResult[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [loadingTimeout, setLoadingTimeout] = useState<NodeJS.Timeout | null>(null);
  const [error, setError] = useState<string | null>(null);

  const debounceRef = useRef<NodeJS.Timeout | null>(null);
  const cacheRef = useRef<Map<string, ArticleSearchResult[]>>(new Map());
  const abortControllerRef = useRef<AbortController | null>(null);

  const searchArticles = useCallback(
    async (query: string) => {
      // Nettoyer les erreurs précédentes
      setError(null);

      // Si la requête est vide ou trop courte
      if (!query || query.trim().length < 2) {
        setSuggestions([]);
        setIsLoading(false);
        return;
      }

      const searchTerm = query.trim().toLowerCase();

      // Vérifier le cache
      if (cacheRef.current.has(searchTerm)) {
        const cachedResults = cacheRef.current.get(searchTerm) || [];
        setSuggestions(cachedResults);
        return;
      }

      // Annuler la requête précédente si elle existe
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }

      // Créer un nouveau AbortController
      abortControllerRef.current = new AbortController();

      // Clear le timeout précédent
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }

      // Nouveau timeout pour le debounce
      debounceRef.current = setTimeout(async () => {
        // Délai minimum pour éviter le clignotement du spinner
        const loadingTimer = setTimeout(() => {
          setIsLoading(true);
        }, 150);
        setLoadingTimeout(loadingTimer);

        try {
          const results = await articleService.searchArticles(searchTerm, limit);

          // Vérifier si la requête n'a pas été annulée
          if (!abortControllerRef.current?.signal.aborted) {
            setSuggestions(results);

            // Mettre en cache les résultats
            cacheRef.current.set(searchTerm, results);

            // Limiter la taille du cache (garder les 50 dernières recherches)
            if (cacheRef.current.size > 50) {
              const firstKey = cacheRef.current.keys().next().value;
              if (firstKey !== undefined) {
                cacheRef.current.delete(firstKey);
              }
            }
          }
        } catch (err) {
          if (!abortControllerRef.current?.signal.aborted) {
            console.error("Erreur lors de la recherche d'articles:", err);
            setError("Erreur lors de la recherche d'articles");
            setSuggestions([]);
          }
        } finally {
          if (!abortControllerRef.current?.signal.aborted) {
            // Nettoyer le timeout de loading et arrêter le spinner
            if (loadingTimeout) {
              clearTimeout(loadingTimeout);
            }
            clearTimeout(loadingTimer);
            setIsLoading(false);
          }
        }
      }, debounceMs);
    },
    [limit, debounceMs]
  );

  const clearSuggestions = useCallback(() => {
    setSuggestions([]);
    setError(null);

    // Annuler toute requête en cours
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }

    // Clear le timeout de debounce
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }

    // Clear le timeout de loading
    if (loadingTimeout) {
      clearTimeout(loadingTimeout);
    }

    setIsLoading(false);
  }, [loadingTimeout]);

  return {
    suggestions,
    isLoading,
    error,
    searchArticles,
    clearSuggestions,
  };
};
